/**
 * Per-plan cap on a campaign's follow-ups (card P1, launch 2026-10-21).
 *
 * PLAN_FEATURES.followUpSteps is the number of follow-ups after the first DM:
 * Basic 3, Pro 5, Custom (agency) 7. A sequence is the locked
 * connection_request and first_dm, then the follow-ups, so Pro's "6 steps"
 * are six messages: the first DM and follow_up_1 to follow_up_5.
 *
 * Enforced server side wherever a campaign's steps are saved (campaigns
 * worker: POST /campaigns, PUT /campaigns/:id with settings.steps,
 * PUT /campaigns/:id/sequence). The builder reads the same numbers.
 */

import { PLAN_FEATURES } from '../constants/plans'
import type { Plan } from '../types/plans'
import { planDisplayName } from './campaign-cap'

/** The two locked opening steps, which are not follow-ups. */
const OPENING_STEP_TYPES: readonly string[] = ['connection_request', 'first_dm']

/** Follow-ups a plan allows after the first DM. */
export function maxFollowUpsFor(plan: Plan): number {
  return PLAN_FEATURES[plan].followUpSteps
}

/** Whole-sequence step cap for a plan: connection request + first DM + follow-ups. */
export function maxSequenceStepsFor(plan: Plan): number {
  return OPENING_STEP_TYPES.length + maxFollowUpsFor(plan)
}

/**
 * Every follow-up status a lead can reach on the largest plan (Custom):
 * followup_1_sent to followup_7_sent. For the status lists that must keep
 * a lead in view after any follow-up (funnel, reply poll, disconnect scan,
 * waiting replies), so a new follow-up never drops out of them again.
 */
export const FOLLOW_UP_SENT_STATUSES: readonly string[] = Array.from(
  { length: maxFollowUpsFor('agency') },
  (_, i) => `followup_${i + 1}_sent`,
)

/** The follow-up number a step type carries ("follow_up_5" is 5), or null. */
export function followUpNumber(type: string | undefined): number | null {
  const m = /^follow_up_(\d+)$/.exec(type ?? '')
  return m ? Number(m[1]) : null
}

/**
 * Why these steps break the plan's follow-up cap, or null when they fit.
 * Counts every step except one connection request and one first DM (legacy
 * value_add and breakup steps count too, and so does a repeated opening
 * step), and also rejects a follow-up numbered past the cap (follow_up_5 on
 * Basic), since its number becomes the lead's status.
 */
export function sequenceCapError(
  steps: ReadonlyArray<{ readonly type?: string } | undefined>,
  plan: Plan,
): string | null {
  const max = maxFollowUpsFor(plan)
  const openings = OPENING_STEP_TYPES.filter((t) => steps.some((s) => s?.type === t)).length
  const followUps = steps.length - openings
  const highest = Math.max(0, ...steps.map((s) => followUpNumber(s?.type) ?? 0))
  if (followUps <= max && highest <= max) return null
  // 'agency' is sold as Custom (plans.ts); planDisplayName predates that.
  const name = plan === 'agency' ? 'Custom' : planDisplayName(plan)
  if (plan === 'starter') {
    return `The ${name} plan allows ${max} follow-ups per campaign. Upgrade to Pro for up to ${maxFollowUpsFor('pro')}.`
  }
  return `The ${name} plan allows ${max} follow-ups per campaign.`
}
